import React, { useEffect, useState } from 'react';
import { feeAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Select from 'react-select';

const FeeMaster = () => {
  const { user } = useAuth();
  const [feeMasters, setFeeMasters] = useState([]);
  const [feeGroups, setFeeGroups] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    fee_groups_id: '',
    feetype_id: '',
    due_date: '',
    amount: '',
    account_type: 'none',
    fine_percentage: '',
    fine_amount: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFeeMasters();
    fetchFeeGroups();
    fetchFeeTypes();
  }, []);

  const fetchFeeMasters = async () => {
    try {
      const response = await feeAPI.getFeeMasters();
      if (response.data.success) {
        setFeeMasters(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching fee masters');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getFeeGroups();
      if (response.data.success) {
        setFeeGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching fee groups:', error);
    }
  };

  const fetchFeeTypes = async () => {
    try {
      const response = await feeAPI.getFeeTypes();
      if (response.data.success) {
        setFeeTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await feeAPI.updateFeeMaster(editingItem.id, formData);
      } else {
        response = await feeAPI.createFeeMaster(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setEditingItem(null);
        setFormData({
          fee_groups_id: '',
          feetype_id: '',
          due_date: '',
          amount: '',
          account_type: 'none',
          fine_percentage: '',
          fine_amount: ''
        });
        fetchFeeMasters();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      fee_groups_id: item.fee_groups_id || '',
      feetype_id: item.feetype_id || '',
      due_date: item.due_date || '',
      amount: item.amount || '',
      account_type: item.account_type || 'none',
      fine_percentage: item.fine_percentage || '',
      fine_amount: item.fine_amount || ''
    });
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete Confirm?')) {
      return;
    }

    try {
      const response = await feeAPI.disableFeeMaster(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchFeeMasters();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting fee master');
    }
  };

  const handleFineTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      account_type: type,
      fine_percentage: type === 'none' ? '' : prev.fine_percentage,
      fine_amount: type === 'none' ? '' : prev.fine_amount
    }));
  };

  const handleAmountOrPercentageChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };

    if (formData.account_type === 'percentage' && field === 'amount') {
      // Calculate fine_amount when amount changes in percentage mode
      if (newFormData.amount && newFormData.fine_percentage) {
        newFormData.fine_amount = ((parseFloat(newFormData.amount) * parseFloat(newFormData.fine_percentage)) / 100).toFixed(2);
      }
    } else if (formData.account_type === 'percentage' && field === 'fine_percentage') {
      // Calculate fine_amount when percentage changes
      if (newFormData.amount && newFormData.fine_percentage) {
        newFormData.fine_amount = ((parseFloat(newFormData.amount) * parseFloat(newFormData.fine_percentage)) / 100).toFixed(2);
      }
    }

    setFormData(newFormData);
  };

  const feeGroupOptions = feeGroups.map(group => ({
    value: group.id,
    label: group.name
  }));

  const feeTypeOptions = feeTypes.map(type => ({
    value: type.id,
    label: type.type || type.name
  }));

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="mt-4 text-gray-600">Loading fee masters...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          <i className="fa fa-money mr-2"></i> Fees Collection
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add/Edit Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Edit' : 'Add'} Fees Master
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fees Group <span className="text-red-500">*</span>
                </label>
                <Select
                  options={feeGroupOptions}
                  value={feeGroupOptions.find(opt => opt.value === formData.fee_groups_id)}
                  onChange={(option) => setFormData({ ...formData, fee_groups_id: option ? option.value : '' })}
                  placeholder="Select"
                  isClearable
                  required
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fees Type <span className="text-red-500">*</span>
                </label>
                <Select
                  options={feeTypeOptions}
                  value={feeTypeOptions.find(opt => opt.value === formData.feetype_id)}
                  onChange={(option) => setFormData({ ...formData, feetype_id: option ? option.value : '' })}
                  placeholder="Select"
                  isClearable
                  required
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                  {formData.account_type !== 'none' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleAmountOrPercentageChange('amount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                  required
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fine Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="account_type"
                      value="none"
                      checked={formData.account_type === 'none'}
                      onChange={(e) => handleFineTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">None</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="account_type"
                      value="percentage"
                      checked={formData.account_type === 'percentage'}
                      onChange={(e) => handleFineTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Percentage</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="account_type"
                      value="fix"
                      checked={formData.account_type === 'fix'}
                      onChange={(e) => handleFineTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Fix Amount</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Percentage
                    {formData.account_type === 'percentage' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    value={formData.fine_percentage}
                    onChange={(e) => handleAmountOrPercentageChange('fine_percentage', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                    disabled={formData.account_type !== 'percentage'}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fine Amount
                    {formData.account_type === 'fix' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    value={formData.fine_amount}
                    onChange={(e) => setFormData({ ...formData, fine_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                    disabled={formData.account_type === 'none' || formData.account_type === 'percentage'}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Fees Master List</h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fees Group</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fees Code</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeMasters.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <a href="#" className="text-primary-600 hover:text-primary-700">
                            {item.fee_group_name || '-'}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <i className="fa fa-money mr-2"></i>
                          {item.fee_type_name}
                        </td>
                        <td className="px-4 py-3">


                          ₹{parseFloat(item.amount).toFixed(2)}

                        </td>
                        <td>

                          <div className="flex justify-end gap-1">
                            <button className="btn-icon" onClick={(e) => { e.preventDefault(); handleEdit(item); }} title="Edit">
                              <FiEdit2 />
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={(e) => { e.preventDefault(); handleDelete(item); }}
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>



                        </td>

                      </tr>
                    ))}
                    {feeMasters.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                          No fee masters found. Add your first fee master using the form.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeMaster;
